// ============================================================================
// budgetRecurringService — Recurring Expense Rules Engine
// ----------------------------------------------------------------------------
// Manages recurring expense rule definitions. Materialisation lands in Slice B2.
// ============================================================================

import { useEffect } from 'react'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { logAuditEvent } from './auditLedgerService'
import { isNoSignInPreviewActive } from '../debug'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import type { BudgetEntityRef, WorkspaceExpense } from '../types/budget'
import { logExpense } from './budgetService'

export const BUDGET_RECURRING_RULES_COLLECTION = 'budget_recurring_rules'

export type BudgetRecurringCadence = 'weekly' | 'monthly' | 'quarterly' | 'yearly'

export type BudgetRecurringRuleTemplate = Partial<WorkspaceExpense> & {
  entityChain?: BudgetEntityRef[]
  entityTitles?: string[]
}

export interface BudgetRecurringRule {
  id: string
  workspaceId: string
  template: BudgetRecurringRuleTemplate
  cadence: BudgetRecurringCadence
  dayOfPeriod: number
  nextRunAt: Timestamp | null
  lastRunAt?: Timestamp | null
  isActive: boolean
  createdAt: Timestamp | null
  createdById: string
}

export interface CreateBudgetRecurringRuleInput {
  workspaceId: string
  template: BudgetRecurringRuleTemplate
  cadence: BudgetRecurringCadence
  dayOfPeriod: number
  actor: { actorId: string; actorName?: string | null }
}

const toBudgetRecurringRule = (id: string, data: DocumentData): BudgetRecurringRule => ({
  id,
  workspaceId: String(data.workspaceId ?? ''),
  template: (data.template as BudgetRecurringRuleTemplate) ?? {},
  cadence: (data.cadence as BudgetRecurringCadence) ?? 'monthly',
  dayOfPeriod: Number(data.dayOfPeriod ?? 1),
  nextRunAt: (data.nextRunAt as Timestamp | null) ?? null,
  lastRunAt: (data.lastRunAt as Timestamp | null) ?? null,
  isActive: data.isActive !== false,
  createdAt: (data.createdAt as Timestamp | null) ?? null,
  createdById: String(data.createdById ?? ''),
})

export const advanceNextRunDate = (
  current: Date,
  cadence: BudgetRecurringCadence,
  dayOfPeriod?: number,
): Date => {
  const next = new Date(current.getTime())
  if (cadence === 'weekly') {
    next.setDate(next.getDate() + 7)
    return next
  }
  if (cadence === 'yearly') {
    next.setFullYear(next.getFullYear() + 1)
    return next
  }

  const monthsToAdd = cadence === 'quarterly' ? 3 : 1
  const targetYear = next.getFullYear() + Math.floor((next.getMonth() + monthsToAdd) / 12)
  const targetMonth = (next.getMonth() + monthsToAdd) % 12

  const daysInTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate()
  const preferredDay = dayOfPeriod ?? next.getDate()
  const clampedDay = Math.min(preferredDay, daysInTargetMonth)

  next.setFullYear(targetYear, targetMonth, clampedDay)
  return next
}

export const subscribeBudgetRecurringRules = (
  workspaceId: string,
  onData: (rules: BudgetRecurringRule[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    onData([])
    return () => {}
  }
  if (isNoSignInPreviewActive()) {
    onData([])
    return () => {}
  }

  const q = query(
    collection(db, BUDGET_RECURRING_RULES_COLLECTION),
    where('workspaceId', '==', workspaceId),
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((docSnap) =>
        toBudgetRecurringRule(docSnap.id, docSnap.data()),
      )
      onData(items)
    },
    (error) => {
      console.warn('[budgetRecurringService] subscription error', { workspaceId }, error)
      onError?.(error)
    },
  )
}

export const createBudgetRecurringRule = async (
  input: CreateBudgetRecurringRuleInput,
  db: Firestore = firestoreDb,
): Promise<string> => {
  if (!input.workspaceId) throw new Error('workspaceId is required')
  if (!input.actor?.actorId) throw new Error('actorId is required')

  const docRef = doc(collection(db, BUDGET_RECURRING_RULES_COLLECTION))
  const cadence = input.cadence ?? 'monthly'
  const dayOfPeriod = Number(input.dayOfPeriod ?? 1)
  const nextRunDate = advanceNextRunDate(new Date(), cadence, dayOfPeriod)

  await setDoc(docRef, {
    workspaceId: input.workspaceId,
    template: input.template ?? {},
    cadence,
    dayOfPeriod,
    nextRunAt: Timestamp.fromDate(nextRunDate),
    isActive: true,
    createdAt: serverTimestamp(),
    createdById: input.actor.actorId,
  })

  return docRef.id
}

export const updateBudgetRecurringRule = async (
  id: string,
  patch: Partial<BudgetRecurringRule>,
  db: Firestore = firestoreDb,
): Promise<void> => {
  if (!id) throw new Error('Rule ID is required')
  const docRef = doc(db, BUDGET_RECURRING_RULES_COLLECTION, id)
  const clean: Record<string, unknown> = {}
  if (patch.template !== undefined) clean.template = patch.template
  if (patch.cadence !== undefined) clean.cadence = patch.cadence
  if (patch.dayOfPeriod !== undefined) clean.dayOfPeriod = patch.dayOfPeriod
  if (patch.isActive !== undefined) clean.isActive = patch.isActive

  await updateDoc(docRef, clean)
}

export const pauseBudgetRecurringRule = async (
  id: string,
  actor: { actorId: string; actorName?: string | null },
  db: Firestore = firestoreDb,
): Promise<void> => {
  const docRef = doc(db, BUDGET_RECURRING_RULES_COLLECTION, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) return
  const data = snap.data()
  await updateDoc(docRef, { isActive: false, updatedAt: serverTimestamp() })
  await runTransaction(db, async (txn) => {
    const wsRef = doc(db, 'workspaces', String(data.workspaceId ?? ''))
    const wsSnap = await txn.get(wsRef)
    logAuditEvent(db, txn, wsSnap.data(), {
      workspaceId: String(data.workspaceId ?? ''),
      actorId: actor.actorId,
      actorName: actor.actorName ?? null,
      action: 'recurring.update',
      activityType: 'UPDATE',
      targetId: id,
      nodeType: 'BUDGET_RECURRING_RULE',
      targetTitle: String(data.template?.description ?? 'Recurring rule'),
      summary: 'Paused recurring expense rule',
      payload: { id, isActive: false },
    })
  })
}

export const resumeBudgetRecurringRule = async (
  id: string,
  actor: { actorId: string; actorName?: string | null },
  db: Firestore = firestoreDb,
): Promise<void> => {
  const docRef = doc(db, BUDGET_RECURRING_RULES_COLLECTION, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) return
  const data = snap.data()
  await updateDoc(docRef, { isActive: true, updatedAt: serverTimestamp() })
  await runTransaction(db, async (txn) => {
    const wsRef = doc(db, 'workspaces', String(data.workspaceId ?? ''))
    const wsSnap = await txn.get(wsRef)
    logAuditEvent(db, txn, wsSnap.data(), {
      workspaceId: String(data.workspaceId ?? ''),
      actorId: actor.actorId,
      actorName: actor.actorName ?? null,
      action: 'recurring.update',
      activityType: 'UPDATE',
      targetId: id,
      nodeType: 'BUDGET_RECURRING_RULE',
      targetTitle: String(data.template?.description ?? 'Recurring rule'),
      summary: 'Resumed recurring expense rule',
      payload: { id, isActive: true },
    })
  })
}

export const deleteBudgetRecurringRule = async (
  id: string,
  actor: { actorId: string; actorName?: string | null },
  db: Firestore = firestoreDb,
): Promise<void> => {
  const docRef = doc(db, BUDGET_RECURRING_RULES_COLLECTION, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) return
  const data = snap.data()
  await deleteDoc(docRef)
  await runTransaction(db, async (txn) => {
    const wsRef = doc(db, 'workspaces', String(data.workspaceId ?? ''))
    const wsSnap = await txn.get(wsRef)
    logAuditEvent(db, txn, wsSnap.data(), {
      workspaceId: String(data.workspaceId ?? ''),
      actorId: actor.actorId,
      actorName: actor.actorName ?? null,
      action: 'recurring.deactivate',
      activityType: 'DELETE',
      targetId: id,
      nodeType: 'BUDGET_RECURRING_RULE',
      targetTitle: String(data.template?.description ?? 'Recurring rule'),
      summary: 'Deleted recurring expense rule',
      payload: { id },
    })
  })
}

export const deactivateBudgetRecurringRule = async (
  id: string,
  actor: { actorId: string; actorName?: string | null },
  db: Firestore = firestoreDb,
): Promise<void> => {
  return pauseBudgetRecurringRule(id, actor, db)
}

export const materialiseDueRecurringRules = async (
  input: {
    workspaceId: string
    actor?: { actorId: string; actorName?: string | null } | null
    now?: Date | number
  },
  db: Firestore = firestoreDb,
): Promise<{ materialised: number }> => {
  if (!input.workspaceId || isNoSignInPreviewActive()) return { materialised: 0 }
  const nowMs = typeof input.now === 'number' ? input.now : (input.now ? input.now.getTime() : Date.now())
  const nowTimestamp = Timestamp.fromMillis(nowMs)

  const q = query(
    collection(db, BUDGET_RECURRING_RULES_COLLECTION),
    where('workspaceId', '==', input.workspaceId),
    where('isActive', '==', true),
    where('nextRunAt', '<=', nowTimestamp),
  )

  const snap = await getDocs(q)
  if (snap.empty) return { materialised: 0 }

  let materialised = 0

  for (const docSnap of snap.docs) {
    const initialRule = toBudgetRecurringRule(docSnap.id, docSnap.data())
    const template = initialRule.template ?? {}

    // (a) Skip rules without template.entityChain. Leave them untouched; console.warn only.
    if (!template.entityChain || !Array.isArray(template.entityChain) || template.entityChain.length === 0) {
      console.warn('[budgetRecurringService] skipping recurring rule without entityChain', docSnap.id)
      continue
    }

    // (b) In a transaction, re-read the rule. Proceed only if isActive and nextRunAt <= now.
    // Set nextRunAt to the next date and lastRunAt (this claims the run, so two open tabs can't double-post).
    const ruleRef = doc(db, BUDGET_RECURRING_RULES_COLLECTION, docSnap.id)
    let claimedRule: BudgetRecurringRule | null = null

    try {
      await runTransaction(db, async (txn) => {
        const freshSnap = await txn.get(ruleRef)
        if (!freshSnap.exists()) return
        const freshData = freshSnap.data()
        const isActive = freshData.isActive !== false
        const freshNextRunAt = freshData.nextRunAt as Timestamp | null
        if (!isActive || !freshNextRunAt) return
        const nextRunMs = typeof freshNextRunAt.toMillis === 'function'
          ? freshNextRunAt.toMillis()
          : (freshNextRunAt.toDate ? freshNextRunAt.toDate().getTime() : 0)
        if (nextRunMs > nowMs) {
          return // already claimed
        }

        const freshRule = toBudgetRecurringRule(freshSnap.id || docSnap.id, freshData)
        const currentNextRunDate = freshRule.nextRunAt?.toDate ? freshRule.nextRunAt.toDate() : new Date(nowMs)
        const nextDate = advanceNextRunDate(currentNextRunDate, freshRule.cadence, freshRule.dayOfPeriod)

        txn.update(ruleRef, {
          nextRunAt: Timestamp.fromDate(nextDate),
          lastRunAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        claimedRule = freshRule
      })
    } catch (err) {
      console.warn('[budgetRecurringService] transaction claim failed for rule', docSnap.id, err)
      continue
    }

    if (!claimedRule) {
      continue
    }

    // (c) After the claim commits, call logExpense with the template's entityChain,
    // entityTitles, amount, currency, category, recurringId and actor.
    try {
      const ruleToPost: BudgetRecurringRule = claimedRule
      await logExpense(
        {
          workspaceId: input.workspaceId,
          entityChain: template.entityChain,
          entityTitles: template.entityTitles,
          amount: Number(template.amount ?? 0),
          currency: String(template.currency ?? 'USD').toUpperCase(),
          description: String(template.description ?? 'Recurring expense'),
          category: template.category,
          vendorId: template.vendorId ?? null,
          taxAmount: template.taxAmount ?? null,
          taxInclusive: template.taxInclusive !== false,
          paymentMethod: template.paymentMethod ?? null,
          tags: Array.isArray(template.tags) ? template.tags : [],
          isIncome: Boolean(template.isIncome),
          recurringId: ruleToPost.id,
          audit: {
            actorId: input.actor?.actorId ?? ruleToPost.createdById ?? 'system',
            actorName: input.actor?.actorName ?? 'Recurring Engine',
          },
        },
        db,
      )
      materialised++
    } catch (err) {
      console.warn('[budgetRecurringService] logExpense failed for claimed rule', (claimedRule as BudgetRecurringRule).id, err)
    }
  }

  return { materialised }
}

export const useBudgetRecurringTick = (
  workspaceId: string,
  actor?: { actorId: string; actorName?: string | null } | null,
) => {
  useEffect(() => {
    if (!workspaceId || !testingOverrideAllowsAllFeatures()) return
    void materialiseDueRecurringRules({ workspaceId, actor }).catch((err) => {
      console.warn('[useBudgetRecurringTick] tick failure', err)
    })
    const timer = setInterval(() => {
      void materialiseDueRecurringRules({ workspaceId, actor }).catch((err) => {
        console.warn('[useBudgetRecurringTick] tick failure', err)
      })
    }, 60000)
    return () => clearInterval(timer)
  }, [workspaceId, actor])
}
